/**
 * conversationManager.js
 * 
 * Orchestrates the LLM conversation loop.
 * 1. Checks conversation state (Active -> Ambient -> Exhausted).
 * 2. Assembles the 3-layer prompt using promptAssembler.
 * 3. Calls the Vertex Client (which routes to the correct model tier).
 * 4. Parses the JSON response and updates the GameState store.
 */

import { fetchNpcResponse, fetchStructuredResponse, fetchSummary } from '../api/vertexClient.js';
import { assemblePrompt, assemblePromptParts, buildLayer2StatePayload, buildStructuredRequest } from '../api/promptAssembler.js';
import { useGameStore } from './gameState.js';
import { getScene } from '../content/sceneManifest.js';
import { useEvalStore } from '../evals/evalStore.js';

// Session conversation state is held in-process per NPC for the demo; the
// durable rolling case summary is persisted to the gameState npcMemory slice
// (see persistConversationMemory) so a re-engagement resumes. The live LLM
// turns run through vertexClient -> the generateProxy Cloud Function; Firestore
// holds server-side usage/cost accounting, not this transcript.
const sessionConversations = {};

/**
 * Evaluates the 3-state cost ceiling machine for a given NPC session.
 */
function getConversationState(sessionState, characterDef) {
  const { revealedInfo, ambientBufferRemaining } = sessionState;
  const { knowledgeItems } = characterDef;

  const allRevealed = knowledgeItems.every((item) => revealedInfo.includes(item));

  if (!allRevealed) return "active";
  if (ambientBufferRemaining > 0) return "ambient";
  return "exhausted";
}

/**
 * Initializes a session object for a new NPC conversation.
 */
function initSession(npcId) {
  if (!sessionConversations[npcId]) {
    sessionConversations[npcId] = {
      revealedInfo: [],
      ambientBufferRemaining: 10,
      history: [],
      caseSummary: '',
    };
    // Chunk 12: resume the persisted rolling case summary from the npcMemory
    // slice via the seed accessor so a re-engagement continues prior context
    // instead of starting blank. Reads defensively (defaults to empty).
    try {
      const mem = useGameStore.getState().getNpcMemory(npcId);
      seedConversationMemory(npcId, { caseSummary: mem.caseSummary || '' });
    } catch (err) {
      console.error('Seed conversation memory failed', err);
    }
  }
  return sessionConversations[npcId];
}

export function getConversationMemory(npcId) {
  const session = sessionConversations[npcId];
  if (!session) return null;
  return { caseSummary: session.caseSummary };
}

export function seedConversationMemory(npcId, memory) {
  // Seed into the bare session record directly (do not call initSession, which
  // calls back into this function — that would recurse).
  const session = sessionConversations[npcId];
  if (!session) return;
  if (memory?.caseSummary) {
    session.caseSummary = memory.caseSummary;
  }
}

export function seedOpeningLine(npcId, line) {
  const session = initSession(npcId);
  session.history.push({ input: '(opening)', response: line });
}

/**
 * Persist a session's durable memory (the rolling case summary) into the
 * gameState npcMemory slice so a later re-engagement resumes it. Verbatim
 * history is never stored (the summary captures it), per the interview-bible
 * memoryPersistence schema.
 */
function persistConversationMemory(npcId) {
  try {
    const mem = getConversationMemory(npcId);
    if (!mem) return;
    useGameStore.getState().setNpcMemory(npcId, { caseSummary: mem.caseSummary || '' });
  } catch (err) {
    console.error('Persist conversation memory failed', err);
  }
}

/**
 * Called on scene transitions to flush the remaining verbatim history
 * into the running summary, preventing context loss across location changes,
 * then write the durable summary back to the npcMemory slice.
 */
export function flushConversationHistory() {
  for (const npcId in sessionConversations) {
    const session = sessionConversations[npcId];
    if (session.history.length > 0) {
      const inputLabel = npcId === 'craine' ? '[Chip]' : '[Craine]';
      const turnsText = session.history.map(h => `${inputLabel}: ${h.input}\n[${npcId}]: ${h.response}`).join('\n\n');

      // Fire and forget, update state in background, then persist to the slice.
      fetchSummary(turnsText, session.caseSummary).then(newSummary => {
        session.caseSummary = newSummary;
        persistConversationMemory(npcId);
      }).catch(err => console.error("Flush summary failed", err));

      session.history = []; // Clear verbatim history for the new scene
    } else {
      // No pending verbatim turns, but persist whatever summary we hold so a
      // re-engagement resumes the latest state.
      persistConversationMemory(npcId);
    }
  }
}

/**
 * Main dialogue handler. Called when the player inputs text meant for an NPC.
 * 
 * @param {string} npcId - The character being spoken to
 * @param {string} input - The player's raw string input
 * @param {Object} characterDef - The imported character definition object
 * @returns {Promise<void>} 
 */
export async function handlePlayerDialogue(npcId, input, characterDef) {
  const store = useGameStore.getState();
  const session = initSession(npcId);
  const state = getConversationState(session, characterDef);

  // If Exhausted, intercept and use deterministic responses without LLM.
  if (state === "exhausted") {
    const responses = characterDef.exhaustionResponses || ["I've said everything I know."];
    // Cycle through deterministic responses
    const nextResponse = responses[Math.floor(Math.random() * responses.length)];
    store.addDialogueLine({ text: input, type: 'player' });
    store.addDialogueLine({ text: nextResponse, type: 'npc' });
    // Record the deterministic line as the NPC's/Craine's spoken utterance so
    // later prompts know it was said and continuity holds.
    session.history.push({ input, response: nextResponse });
    // Craine interception is handled at the UI layer if visiting an exhausted NPC.
    return;
  }

  // (Player input echo is handled at the Terminal layer)

  // Prepare states for the Assembler
  const characterStateForPrompt = {
    name: characterDef.name,
    trustScore: store.getTrust(npcId),
    interactionCount: session.history.length,
    emotionalState: "neutral", // Pull from session if tracking dynamic mood
    history: session.history.map(h => {
      const inputLabel = npcId === 'craine' ? '[Chip]' : '[Craine]';
      return `${inputLabel}: ${h.input}\n[${characterDef.name}]: ${h.response}`;
    }).join('\n\n'),
    evidenceShown: store.evidenceShown[npcId] || [],
    latestInput: input,
    caseSummary: session.caseSummary
  };

  const scene = getScene(store.sceneId);
  const gameStateForPrompt = {
    chapter: store.chapter,
    sceneId: store.sceneId,
    sceneDescription: scene?.description || "Current Scene",
    sceneHotspots: scene?.hotspots?.map(h => h.id || h.label).join(", ") || "None visible",
    interferencePhase: store.interferencePhase
  };

  // ── Prompt assembly: structured (server-assembled) vs legacy (client-assembled) ──
  //
  // Two paths:
  //   STRUCTURED (Shape A) — phaseId-based defs (e.g. getPersona('crainePhaseA'), generic NPC
  //     defs). The client sends references plus spoiler-free dynamic state; the
  //     server holds Layer 1 / Layer 3 / case knowledge and assembles them. This
  //     manager no longer imports layer3Manifest or craineCaseKnowledge.
  //   LEGACY (Shape C) — defs that carry a pre-composed promptText (the App.jsx
  //     car briefing/debrief def, and the ambient buffer). These remain on the
  //     transitional client-assembled path until the car-path cutover. The
  //     ambient prompt is fully client-composed text and never carries full-game
  //     canon, so it stays here too.
  const isCraineScene = npcId === 'craine';
  let isAmbient = false;
  let jsonResponse;

  // Snapshot trust before the turn so the evals layer can attribute movement.
  const trustBefore = store.getTrust(npcId);

  // Streaming wire-through: as the model's "dialogue" field streams in, render
  // it as a provisional npc line (typing feel). The finalized parsed response
  // replaces it via the normal addDialogueLine path, so evals, trust, and
  // memory are untouched. If onToken never fires (callable fallback), streamed
  // stays empty and behavior is identical to today.
  let streamedText = '';
  const onToken = (text) => {
    if (!text) return;
    streamedText += text;
    store.setStreamingLine({ speaker: characterDef.name, text: streamedText });
  };

  try {
    if (state === "ambient") {
      // LEGACY (Shape C): ambient buffer. Client-assembled spoiler-free text.
      isAmbient = true;
      const ambientDef = { promptText: characterDef.ambientPrompt };
      const promptText = assemblePrompt(ambientDef, characterStateForPrompt, gameStateForPrompt, { isCraineScene });
      jsonResponse = await fetchNpcResponse(npcId, promptText, characterDef.modelTier, isAmbient, null);
    } else if (characterDef.carBody !== undefined) {
      // STRUCTURED (Shape A) car def: the Layer 3 identity is held server-side.
      // The client sends carBody (scene instructions + stable knowledge block,
      // no identity text) plus references; the server prepends getLayer3Text
      // and assembles. carVolatile carries the per-turn knowledge and lands at
      // the end of the user content so the systemInstruction stays byte-stable
      // for Gemini's implicit prefix cache.
      const layer2State = buildLayer2StatePayload(characterStateForPrompt, gameStateForPrompt);
      const structured = buildStructuredRequest({
        kind: 'craine',
        phase: characterDef.phase,
        isCraineScene: true,
        carBody: characterDef.carBody,
        carVolatile: characterDef.carVolatile,
        layer2State,
        userInput: input,
        recentInputs: session.history.slice(-2).map(h => h.input),
      });
      jsonResponse = await fetchStructuredResponse(npcId, structured, characterDef.modelTier, false, onToken);
    } else if (characterDef.promptText) {
      // LEGACY (Shape C): a pre-composed def (ambient fallback / defensive). The
      // promptText already carries identity + scene instructions + case facts,
      // so we client-assemble the system instruction and send it as-is.
      const { systemInstruction, userContent } = assemblePromptParts(characterDef, characterStateForPrompt, gameStateForPrompt, { isCraineScene });
      jsonResponse = await fetchNpcResponse(npcId, userContent, characterDef.modelTier, false, systemInstruction);
    } else {
      // STRUCTURED (Shape A): references + dynamic state. The server assembles
      // Layer 1 + Layer 3 (+ phase-gated case facts for Craine scenes).
      const layer2State = buildLayer2StatePayload(characterStateForPrompt, gameStateForPrompt);
      const structured = isCraineScene
        ? buildStructuredRequest({
            kind: 'craine',
            // getPersona('crainePhaseA').phaseId is the manifest key (e.g. 'craine_phase_a');
            // it doubles as both the layer3 identity key and the case-knowledge
            // phase value the server passes through retrieveChunks/format, exactly
            // as this manager did before the move.
            phaseId: characterDef.phaseId,
            phase: characterDef.phaseId,
            isCraineScene: true,
            layer2State,
            userInput: input,
            recentInputs: session.history.slice(-2).map(h => h.input),
          })
        : buildStructuredRequest({
            kind: 'npc',
            phaseId: characterDef.phaseId,
            isCraineScene: false,
            layer2State,
            userInput: input,
          });
      jsonResponse = await fetchStructuredResponse(npcId, structured, characterDef.modelTier, false, onToken);
    }

    // Finalize: drop the provisional streaming line before the real one lands.
    store.setStreamingLine(null);

    // Session cap hit — propagate to caller so the demo can end gracefully.
    if (jsonResponse.__sessionExhausted) {
      return { __sessionExhausted: true };
    }

    // Score this turn (deterministic evaluators run synchronously; the optional
    // LLM judge fires asynchronously inside the store).
    useEvalStore.getState().recordTurn({
      playerInput: input,
      response: jsonResponse,
      npcId,
      trustBefore,
    });

    // Update state based on schema response. A streamed turn already typed the
    // text in place, so the finalized line renders instantly (no second
    // typewriter pass); a non-streamed turn typewrites exactly as today.
    store.addDialogueLine({ text: jsonResponse.dialogue, type: 'npc', speaker: characterDef.name, instant: streamedText.length > 0 });

    if (jsonResponse.trust_delta) {
      store.updateTrust(npcId, jsonResponse.trust_delta);
      console.log(`Trust delta for ${npcId}: ${jsonResponse.trust_delta}`);
    }

    // Process revealed information and flags
    if (jsonResponse.revealed && jsonResponse.revealed.length > 0) {
      const newReveals = jsonResponse.revealed.filter(r => !session.revealedInfo.includes(r));
      session.revealedInfo.push(...newReveals);
    }

    // Maintain session history for next turn context
    session.history.push({ input, response: jsonResponse.dialogue });

    // --- ROLLING SUMMARIZATION LOGIC ---
    // If the verbatim history grows beyond our threshold (e.g., 6 turns),
    // extract the oldest 3 turns, summarize them, and remove them from the active array.
    if (session.history.length >= 6) {
      const turnsToSummarize = session.history.slice(0, 3);
      const sumInputLabel = npcId === 'craine' ? '[Chip]' : '[Craine]';
      const turnsText = turnsToSummarize.map(h => `${sumInputLabel}: ${h.input}\n[${characterDef.name}]: ${h.response}`).join('\n\n');

      fetchSummary(turnsText, session.caseSummary).then(newSummary => {
        session.caseSummary = newSummary;
      }).catch(err => console.error("Rolling summary failed", err));

      session.history.splice(0, 3);
      useEvalStore.getState().recordSummarization(3);
    }

    // Decrement buffer if ambient
    if (state === "ambient") {
      session.ambientBufferRemaining--;
    }

  } catch (error) {
    store.setStreamingLine(null);
    console.error("Conversation Manager error:", error);
    const faultLine = "[System Fault: Dialogue connection lost. Offline fallback.]";
    store.addDialogueLine({ text: faultLine, type: 'interference' });
    // Record the fallback as the spoken line so continuity holds: later prompts
    // see that this turn produced an utterance rather than a silent gap.
    session.history.push({ input, response: faultLine });
  }
}
